import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Loader2,
  ArrowLeft,
  CreditCard,
  CheckCircle,
  AlertCircle,
  ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useCartStore } from "@/stores/cartStore";
import { apiClient } from "@/services";

/**
 * Página de procesamiento de pago
 *
 * Maneja tanto ventanas emergentes como redirecciones directas a Transbank
 */
export function PaymentProcessingPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { clearCart } = useCartStore();

  // Obtener parámetros de la URL
  const token = searchParams.get("token") || searchParams.get("token_ws");
  const amount = searchParams.get("amount");
  const source = searchParams.get("source");
  const itemsParam = searchParams.get("items");

  const [isChecking, setIsChecking] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [paymentAmount, setPaymentAmount] = useState<string | null>(amount);
  const [lastCheckTime, setLastCheckTime] = useState<Date | null>(null);
  const [nextCheckTime, setNextCheckTime] = useState<Date | null>(null);
  const isProcessingHangingRef = useRef(false);
  const hasScheduledHangingRef = useRef(false);

  // Determinar si es redirección directa o ventana emergente
  // Si no hay source o es "transbank", consideramos que es redirección directa
  const isDirectRedirect = !source || source === "transbank";

  // Parsear los items del carrito si están disponibles
  const cartItems = itemsParam
    ? JSON.parse(decodeURIComponent(itemsParam))
    : [];

  useEffect(() => {
    if (token) {
      // Iniciar verificación del estado del pago
      checkPaymentStatus();

      // Para redirecciones directas, verificar periódicamente
      if (isDirectRedirect) {
        // Programar la próxima verificación
        const nextCheck = new Date();
        nextCheck.setSeconds(nextCheck.getSeconds() + 10);
        setNextCheckTime(nextCheck);
        
        const interval = setInterval(() => {
          checkPaymentStatus();
          
          // Actualizar la próxima verificación
          const newNextCheck = new Date();
          newNextCheck.setSeconds(newNextCheck.getSeconds() + 10);
          setNextCheckTime(newNextCheck);
        }, 10000); // Verificar cada 10 segundos

        return () => clearInterval(interval);
      }
    } else {
      setError("No se encontró el token de transacción");
    }
  }, [token, isDirectRedirect]);

  const checkPaymentStatus = async () => {
    if (!token) return;

    setIsChecking(true);
    setError(null);

    try {
      console.log("🔄 Verificando estado del pago para token:", token);

      const response = await apiClient.get(`/api/v1/payments/status/${token}`);
      const status = response.data;

      console.log("🔄 Respuesta del estado:", status);

      if (status.success && status.data) {
        const details = status.data;
        setPaymentStatus(details.status);
        
        // Actualizar el monto si está disponible en la respuesta
        if (details.amount && !paymentAmount) {
          setPaymentAmount(details.amount.toString());
        }
        
        setLastCheckTime(new Date());

        // Manejar diferentes estados
        if (details.status === "AUTHORIZED") {
          handleSuccessfulPayment(details);
        } else if (details.status === "REJECTED") {
          handleRejectedPayment();
        } else if (details.status === "PENDING") {
          // Para redirecciones directas, intentar procesar el pago después de un tiempo
          if (isDirectRedirect && !hasScheduledHangingRef.current) {
            hasScheduledHangingRef.current = true;
            setTimeout(() => {
              hasScheduledHangingRef.current = false;
              processHangingPayment();
            }, 5000); // Esperar 5 segundos antes de intentar procesar
          }
        }
      } else {
        setError("No se pudo obtener el estado del pago");
      }
    } catch (error: any) {
      console.error("❌ Error verificando estado del pago:", error);

      // Si es error 429, mostrar mensaje específico
      if (error.response?.status === 429) {
        setError(
          "Demasiadas solicitudes. Intenta nuevamente en unos momentos."
        );
      } else {
        setError("Error al verificar el estado del pago");
      }
    } finally {
      setIsChecking(false);
    }
  };

  const handleSuccessfulPayment = async (details: any) => {
    console.log("✅ Pago autorizado:", details);

    // Limpiar carrito
    clearCart();

    try {
      // Generar token de sesión seguro
      const sessionTokenResponse = await apiClient.post(
        "/api/v1/payments/generate-session-token",
        {
          orderId: details.orderId,
          amount: amount || details.amount,
          authCode: details.authorizationCode,
          items: cartItems,
        }
      );

      if (sessionTokenResponse.data.success) {
        // Redirigir a página de éxito con token seguro
        const successUrl = `/payment/success?token=${sessionTokenResponse.data.data.sessionToken}&source=transbank`;
        window.location.href = successUrl;
      } else {
        // Fallback a URL con parámetros (menos seguro)
        const successUrl = `/payment/success?orderId=${
          details.orderId
        }&amount=${amount || details.amount}&authCode=${
          details.authorizationCode
        }&source=transbank&items=${encodeURIComponent(
          JSON.stringify(cartItems)
        )}`;
        window.location.href = successUrl;
      }
    } catch (error) {
      console.error("❌ Error generando token de sesión:", error);

      // Fallback directo sin token de sesión
      const successUrl = `/payment/success?orderId=${details.orderId}&amount=${
        amount || details.amount
      }&authCode=${
        details.authorizationCode
      }&source=transbank&items=${encodeURIComponent(
        JSON.stringify(cartItems)
      )}`;
      window.location.href = successUrl;
    }
  };

  const handleRejectedPayment = () => {
    console.log("❌ Pago rechazado");

    // Redirigir a página de error
    const errorUrl = `/payment/error?error=REJECTED&message=${encodeURIComponent(
      "El pago fue rechazado por el banco"
    )}&source=transbank`;
    window.location.href = errorUrl;
  };

  const processHangingPayment = async () => {
    if (!token) return;
    if (isProcessingHangingRef.current) {
      console.log("⏳ Ya hay un procesamiento colgado en curso. Ignorando.");
      return;
    }
    isProcessingHangingRef.current = true;

    try {
      console.log("🔄 Procesando pago colgado...");

      const response = await apiClient.post(
        "/api/v1/payments/process-hanging",
        {
          token: token,
        }
      );

      if (response.data.success) {
        console.log("✅ Pago procesado exitosamente:", response.data.data);

        // Limpiar carrito
        clearCart();

        // Generar token de sesión seguro
        const sessionTokenResponse = await apiClient.post(
          "/api/v1/payments/generate-session-token",
          {
            orderId: response.data.data.orderId,
            amount: response.data.data.amount,
            authCode: response.data.data.authorizationCode,
            items: cartItems,
          }
        );

        if (sessionTokenResponse.data.success) {
          // Redirigir a página de éxito con token seguro
          const successUrl = `/payment/success?token=${sessionTokenResponse.data.data.sessionToken}&source=transbank`;
          window.location.href = successUrl;
        } else {
          // Fallback a URL con parámetros
          const successUrl = `/payment/success?orderId=${
            response.data.data.orderId
          }&amount=${response.data.data.amount}&authCode=${
            response.data.data.authorizationCode
          }&source=transbank&items=${encodeURIComponent(
            JSON.stringify(cartItems)
          )}`;
          window.location.href = successUrl;
        }
      } else {
        console.log("❌ Error procesando pago colgado:", response.data);
        setError(response.data.message || "No se pudo procesar el pago");
      }
    } catch (error: any) {
      console.error("❌ Error procesando pago colgado:", error);

      if (error.response?.data?.error === "Pago pendiente") {
        setError(
          "El pago aún está pendiente. Completa el pago en Transbank y vuelve a intentar."
        );
      } else if (error.response?.data?.error === "Pago rechazado") {
        handleRejectedPayment();
      } else {
        setError("Error al procesar el pago. Intenta nuevamente.");
      }
    } finally {
      isProcessingHangingRef.current = false;
    }
  };

  const formatPrice = (price: string | null) => {
    if (!price) return "N/A";
    return new Intl.NumberFormat("es-CL", {
      style: "currency",
      currency: "CLP",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(parseInt(price));
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border bg-card">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/")}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Volver al Inicio
            </Button>
          </div>
        </div>
      </div>

      {/* Contenido Principal */}
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          {/* Estado del Procesamiento */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                Procesando tu Pago
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {isDirectRedirect ? (
                // Caso de redirección directa
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <ExternalLink className="h-5 w-5 text-blue-600" />
                    <div>
                      <p className="font-medium text-blue-800">
                        Pago iniciado en Transbank
                      </p>
                      <p className="text-sm text-blue-600">
                        Completa el pago en la ventana de Transbank y regresa
                        aquí
                      </p>
                      <div className="flex items-center gap-2 mt-2">
                        <Loader2 className="h-3 w-3 animate-spin text-blue-600" />
                        <span className="text-xs text-blue-600">
                          Verificando automáticamente cada 10 segundos
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground">
                      <strong>Monto a pagar:</strong> {formatPrice(paymentAmount)}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      <strong>Token de transacción:</strong>{" "}
                      {token?.substring(0, 20)}...
                    </p>
                    {cartItems.length > 0 && (
                      <p className="text-sm text-muted-foreground">
                        <strong>Productos:</strong> {cartItems.length} item(s)
                      </p>
                    )}
                    {lastCheckTime && (
                      <p className="text-sm text-muted-foreground">
                        <strong>Última verificación:</strong>{" "}
                        {lastCheckTime.toLocaleTimeString()}
                      </p>
                    )}
                    {nextCheckTime && (
                      <p className="text-sm text-muted-foreground">
                        <strong>Próxima verificación:</strong>{" "}
                        {nextCheckTime.toLocaleTimeString()}
                      </p>
                    )}
                  </div>

                  <Separator />

                  <div className="space-y-3">
                    <h4 className="font-medium text-foreground">
                      Estado del pago:
                    </h4>
                    {paymentStatus ? (
                      <Badge
                        variant={
                          paymentStatus === "AUTHORIZED"
                            ? "default"
                            : paymentStatus === "REJECTED"
                            ? "destructive"
                            : "secondary"
                        }
                        className={
                          paymentStatus === "AUTHORIZED"
                            ? "bg-green-100 text-green-800"
                            : paymentStatus === "REJECTED"
                            ? "bg-red-100 text-red-800"
                            : ""
                        }
                      >
                        {paymentStatus === "AUTHORIZED"
                          ? "Autorizado"
                          : paymentStatus === "REJECTED"
                          ? "Rechazado"
                          : paymentStatus === "PENDING"
                          ? "Pendiente"
                          : paymentStatus}
                      </Badge>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span className="text-sm text-muted-foreground">
                          Verificando estado...
                        </span>
                      </div>
                    )}
                  </div>

                  <Separator />

                  <div className="space-y-3">
                    <h4 className="font-medium text-foreground">
                      ¿Qué hacer ahora?
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                        <p>Completa el pago en la ventana de Transbank</p>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                        <p>
                          Regresa a esta página después de completar el pago
                        </p>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                        <p>
                          Verás el resultado de tu transacción automáticamente
                        </p>
                      </div>
                    </div>
                  </div>

                  <Button
                    onClick={checkPaymentStatus}
                    disabled={isChecking}
                    className="w-full"
                  >
                    {isChecking ? (
                      <div className="flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Verificando estado...
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4" />
                        Verificar Estado del Pago
                      </div>
                    )}
                  </Button>

                  <Button
                    onClick={processHangingPayment}
                    variant="outline"
                    className="w-full"
                  >
                    Procesar Pago Completado
                  </Button>
                </div>
              ) : (
                // Caso de ventana emergente
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <div>
                      <p className="font-medium text-green-800">
                        Ventana de pago abierta
                      </p>
                      <p className="text-sm text-green-600">
                        Completa el pago en la ventana emergente
                      </p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground">
                      <strong>Monto a pagar:</strong> {formatPrice(amount)}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      <strong>Token de transacción:</strong>{" "}
                      {token?.substring(0, 20)}...
                    </p>
                    {cartItems.length > 0 && (
                      <p className="text-sm text-muted-foreground">
                        <strong>Productos:</strong> {cartItems.length} item(s)
                      </p>
                    )}
                  </div>

                  <Separator />

                  <div className="space-y-3">
                    <h4 className="font-medium text-foreground">
                      Estado actual:
                    </h4>
                    {paymentStatus ? (
                      <Badge
                        variant={
                          paymentStatus === "AUTHORIZED"
                            ? "default"
                            : paymentStatus === "REJECTED"
                            ? "destructive"
                            : "secondary"
                        }
                        className={
                          paymentStatus === "AUTHORIZED"
                            ? "bg-green-100 text-green-800"
                            : paymentStatus === "REJECTED"
                            ? "bg-red-100 text-red-800"
                            : ""
                        }
                      >
                        {paymentStatus === "AUTHORIZED"
                          ? "Autorizado"
                          : paymentStatus === "REJECTED"
                          ? "Rechazado"
                          : paymentStatus === "PENDING"
                          ? "Pendiente"
                          : paymentStatus}
                      </Badge>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span className="text-sm text-muted-foreground">
                          Verificando estado...
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {error && (
                <div className="flex items-start gap-2 p-3 bg-destructive/5 border border-destructive/20 rounded-lg">
                  <AlertCircle className="h-4 w-4 text-destructive mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-destructive">{error}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Información adicional */}
          <Card>
            <CardContent className="pt-6">
              <div className="text-center space-y-2">
                <p className="text-sm text-muted-foreground">
                  Si tienes problemas con el pago, contacta a nuestro soporte
                </p>
                <Button
                  variant="outline"
                  onClick={() => navigate("/")}
                  size="sm"
                >
                  Volver a la Tienda
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
